import React from "react";
import dayjs from "dayjs";
import {
  AI_PENDING,
  ALL_PROVIDERS,
  ALL_USERS,
  FAX_DELETED_DAYS,
  ME,
  UNASSIGNED,
  faxProviderChips,
  faxStatusMatches,
  faxUserChips,
} from "../fax/faxWork";

/*
 * Inbox > Faxes filter bar (owner 10-20: "too-small font and disorganised").
 * Used by fax/faxtable.jsx only with `inboxStyle` (the unified Inbox);
 * Communication > Fax and OSA's fax page keep FaxWorkFilters.
 *
 *   row 1  Status tabs with counts: To work / Review / Done / Deleted / All,
 *          and the page actions (Send Fax, list toggle, refresh) at the right
 *   row 2  Assigned to [Anyone / Assigned to me / Unassigned / a user],
 *          Provider [All / a provider / Unassigned] (both apply together:
 *          filters.combine), patient search, day (‹ date ›, cleared = all
 *          days). The Deleted view swaps the assignee select for its window
 *          (received in the last 7 / 30 days); the provider pick narrows it
 *          too (owner 10-22). "All (30 days)" = open + done + deleted (v2 GET
 *          fax/all). Counts in the provider select are the selected tab's.
 * One grid (owner 10-20): every control 40 px, radius 6 px, labels centred; row 2 runs from
 * the left edge of the tabs to the right edge of Send Fax (the search grows); it wraps
 * instead of scrolling.
 */
export const INBOX_FAX_TABS = [
  { id: "todo", label: "To work" },
  { id: "review", label: "Review" },
  { id: "done", label: "Done" },
  { id: "deleted", label: "Deleted" },
  { id: "all", label: "All" },
  { id: "all30", label: "All (30 days)" },
];

// Rows per status tab (the Deleted count is the Deleted view's own list; All
// (30 days) the window total once read, else blank).
export function faxTabCounts(rows, deletedCount, all30Count = null) {
  const list = Array.isArray(rows) ? rows : [];
  const n = (s) => list.filter((r) => faxStatusMatches(r, s)).length;
  return {
    todo: n("todo"),
    review: n("review"),
    done: n("done"),
    deleted: Number(deletedCount) || 0,
    all: n("all"),
    all30: all30Count === null || all30Count === undefined ? "" : Number(all30Count) || 0,
  };
}

const control =
  "h-10 min-w-0 rounded-md border border-[#D0D5DD] bg-white px-3 text-[14px] leading-10 text-[#1D2951] outline-none";

export default function InboxFaxFilters({
  rows,
  // the current view's rows (Deleted / All (30 days) lists) and v2 GET fax/all
  // provider_counts: what the provider counts are made of (owner 10-22)
  viewRows = null,
  serverCounts = null,
  all30Count = null,
  providers = [],
  filters,
  onChange,
  available,
  deletedCount = 0,
  deletedDays = 7,
  onDeletedDays,
  staff = [],
  me = null,
  assignAvailable = false,
  toolbar = null,
  actions = null,
}) {
  const counts = faxTabCounts(rows, deletedCount, all30Count);
  const deletedView = available && filters.status === "deleted";
  const chipRows = viewRows || rows;
  const providerChips = available
    ? faxProviderChips(chipRows, providers, filters.provider, undefined, filters.status, serverCounts)
    : [];
  const userChips = available && assignAvailable ? faxUserChips(chipRows, staff, filters.user, me, filters.status) : [];
  const t = toolbar || {};
  const day = t.selectedDate ? dayjs(t.selectedDate) : null;
  return (
    <div className="mb-2 flex w-full min-w-0 flex-col gap-2 px-1 text-left text-[14px]" data-testid="inbox-fax-filters">
      <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
        {available && (
          <div className="flex min-w-0 max-w-full gap-1 overflow-x-auto pb-0.5" role="tablist" aria-label="Fax status">
            {INBOX_FAX_TABS.map((s) => {
              const on = filters.status === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => onChange({ ...filters, status: s.id })}
                  className={`flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3 text-[14px] ${
                    on ? "border-[#1D2951] bg-[#1D2951] font-semibold text-white" : "border-[#D5D5D5] bg-white text-[#1d2951] hover:bg-[#F5F6FA]"
                  }`}
                  data-testid={`inbox-fax-tab-${s.id}`}
                >
                  {s.label}
                  <span
                    className={`inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 text-[12px] font-semibold leading-5 ${
                      on ? "bg-white/20 text-white" : "bg-[#F2F4F7] text-[#475467]"
                    }`}
                  >
                    {counts[s.id]}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {actions && <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div>}
      </div>

      {/* Row 2: one grid, every cell 40 px; from 1024 px one line from the tabs' left edge to Send
          Fax's right edge (the search takes the slack), two columns from 640 px, stacked below. */}
      <div
        className="grid w-full min-w-0 grid-cols-1 items-center gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(150px,180px)_minmax(150px,180px)_minmax(180px,1fr)_auto]"
        data-testid="inbox-fax-row2"
      >
        {deletedView && (
          <div className="flex h-10 items-center gap-2 text-[14px] text-[#475467]" data-testid="inbox-fax-deleted-days">
            <span className="whitespace-nowrap">{deletedCount} deleted · received in the last</span>
            <select
              value={Number(deletedDays)}
              onChange={(e) => typeof onDeletedDays === "function" && onDeletedDays(Number(e.target.value))}
              className={control}
              aria-label="Deleted window"
            >
              {FAX_DELETED_DAYS.map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </select>
          </div>
        )}
        {available && (
            <>
              {deletedView ? null : assignAvailable ? (
                <select
                  value={filters.user || ALL_USERS}
                  onChange={(e) => onChange({ ...filters, user: e.target.value })}
                  className={`${control} w-full`}
                  aria-label="Assigned to"
                  title="Assigned to"
                  data-testid="inbox-fax-assignee"
                >
                  <option value={ALL_USERS}>Assigned: anyone</option>
                  {userChips
                    .filter((c) => c.key !== ALL_USERS)
                    .map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.key === ME ? "Assigned: me" : c.key === UNASSIGNED ? "Unassigned" : `Assigned: ${c.label}`} ({c.count})
                      </option>
                    ))}
                </select>
              ) : (
                <span className="hidden lg:block" />
              )}
              <select
                value={filters.provider || ALL_PROVIDERS}
                onChange={(e) => onChange({ ...filters, provider: e.target.value })}
                className={`${control} w-full`}
                aria-label="Provider"
                title="Provider"
                data-testid="inbox-fax-provider"
              >
                <option value={ALL_PROVIDERS}>Provider: all</option>
                {providerChips
                  .filter((c) => c.key !== ALL_PROVIDERS)
                  .map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.key === UNASSIGNED ? "No provider" : c.key === AI_PENDING ? "AI pending" : c.label} ({c.count})
                    </option>
                  ))}
              </select>
            </>
        )}
        {toolbar && (
          <>
            <div className={`flex min-w-0 items-center gap-2 ${!available ? "sm:col-span-2 lg:col-span-1" : ""}`}>
              <input
                type="search"
                value={t.search || ""}
                onChange={(e) => t.setSearch && t.setSearch(e.target.value)}
                className={`${control} w-full`}
                placeholder="Search patient or phone"
                aria-label="Search faxes"
              />
              {(t.search || day) && (
                <button
                  type="button"
                  onClick={() => {
                    if (t.setSearch) t.setSearch("");
                    if (t.setSelectedDate) t.setSelectedDate(null);
                  }}
                  className="h-10 shrink-0 rounded-md px-2 text-[14px] text-[#475467] hover:text-[#1D2951]"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex h-10 items-stretch" data-testid="inbox-fax-date">
              <button
                type="button"
                onClick={() => t.setSelectedDate && t.setSelectedDate((day || dayjs()).subtract(1, "day"))}
                className="flex w-10 shrink-0 items-center justify-center rounded-l-md border border-r-0 border-[#D0D5DD] bg-white text-[16px] text-[#475467]"
                aria-label="Previous day"
              >
                ‹
              </button>
              <input
                type="date"
                value={day ? day.format("YYYY-MM-DD") : ""}
                onChange={(e) => t.setSelectedDate && t.setSelectedDate(e.target.value ? dayjs(e.target.value) : null)}
                className="h-10 min-w-0 flex-1 border border-[#D0D5DD] bg-white px-2 text-[14px] text-[#1D2951] outline-none lg:w-[140px] lg:flex-none"
                aria-label="Day"
              />
              <button
                type="button"
                onClick={() => t.setSelectedDate && t.setSelectedDate((day || dayjs()).add(1, "day"))}
                className="flex w-10 shrink-0 items-center justify-center rounded-r-md border border-l-0 border-[#D0D5DD] bg-white text-[16px] text-[#475467]"
                aria-label="Next day"
              >
                ›
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
